import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import { getImageURL } from "../../helpers";
import { useDispatch } from "react-redux";
import {
  setActiveAnnouncement,
  setAnnouncementView,
  setIsShowAnnouncements,
} from "../../store/Slice/announcementSlice";

const AnnouncementsCarousel = ({
  banners = [],
  tabValue = 0,
  isShow,
  interval = 7000,
}) => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  // Preload images
  useEffect(() => {
    const preloadImages = async () => {
      setIsLoading(true);
      const imagePromises = banners.map((banner) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = getImageURL(banner.poster);
          img.onload = () => {
            setLoadedImages(
              (prev) => new Set([...prev, getImageURL(banner.poster)])
            );
            resolve();
          };
          img.onerror = reject;
        });
      });

      try {
        await Promise.all(imagePromises);
      } catch (error) {
        console.error("Error preloading images:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (banners.length > 0) {
      preloadImages();
    }
  }, [banners]);

  // Auto-slide functionality
  useEffect(() => {
    if (isLoading) return;

    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, interval);

    return () => clearInterval(timer);
  }, [banners.length, interval, isLoading]);

  const handleIndicatorClick = (index) => {
    setCurrentBanner(index);
  };

  if (banners.length === 0 || !isShow || tabValue !== 0) {
    return null;
  }

  return (
    <CarouselContainer>
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}
      <BannerContainer>
        {banners.map((banner, index) => (
          <BannerSlide key={banner.id} active={index === currentBanner}>
            {loadedImages.has(getImageURL(banner.poster)) && (
              <BannerImage
                src={getImageURL(banner.poster)}
                alt={banner.title || `Banner ${index + 1}`}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(setAnnouncementView("details"));
                  dispatch(setActiveAnnouncement(banner.id));
                  dispatch(setIsShowAnnouncements(false));
                }}
              />
            )}
          </BannerSlide>
        ))}
      </BannerContainer>
      <IndicatorsContainer>
        {banners.map((_, index) => (
          <Box
            key={index}
            onClick={() => handleIndicatorClick(index)}
            sx={{ cursor: "pointer" }}
          >
            {index === currentBanner ? (
              <ProgressIndicator>
                <ProgressBar duration={interval} />
              </ProgressIndicator>
            ) : (
              <DotIndicator />
            )}
          </Box>
        ))}
      </IndicatorsContainer>
    </CarouselContainer>
  );
};

export default AnnouncementsCarousel;

const CarouselContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  //   maxWidth: "1200px",
  //   margin: "0 auto",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius,
}));

const BannerContainer = styled(Box)({
  position: "relative",
  height: "500px",
  "@media (max-width: 768px)": {
    height: "250px",
  },
});

const BannerSlide = styled(Box)(({ active }) => ({
  position: "absolute",
  width: "100%",
  height: "100%",
  opacity: active ? 1 : 0,
  transform: active ? "translateX(0)" : "translateX(100%)",
  transition: "opacity 500ms ease-in-out, transform 500ms ease-in-out",
}));

const BannerImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const IndicatorsContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  zIndex: 1,
  padding: "10px",
  backgroundColor: "#ffffff",
});

const ProgressIndicator = styled(Box)(({ theme }) => ({
  width: "20px",
  height: "10px",
  //backgroundColor: "rgba(255, 255, 255, 0.3)",
  backgroundColor: "#000",
  borderRadius: "6px",
  overflow: "hidden",
  cursor: "pointer",
}));

const ProgressBar = styled(Box)(({ duration }) => ({
  height: "100%",
  backgroundColor: "grey",
  width: "100%",
  transform: "translateX(-100%)",
  animation: `progress ${duration}ms linear infinite`,
  "@keyframes progress": {
    "0%": {
      transform: "translateX(-100%)",
    },
    "100%": {
      transform: "translateX(0)",
    },
  },
}));

const DotIndicator = styled(Box)({
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  backgroundColor: "grey",
  cursor: "pointer",
  transition: "background-color 200ms ease",
  "&:hover": {
    backgroundColor: "grey",
  },
});

const LoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.1)",
  zIndex: 2,
});
